import type { ReactNode } from "react";
import clsx from "clsx";

import styles from "./tab-bar.module.css";

export interface TabBarProps {
  "aria-label": string;
  /**
   * Already-rendered link elements — the caller's own router `Link`, one per
   * tab — not link descriptors. See the note below for why.
   */
  children: ReactNode;
}

/**
 * A presentational tab bar for route-backed tabs (SR-301 phase 8 doc §4k,
 * G-05, reshaped). Retires `tasks/_layout.tsx`'s hand-rolled `<nav
 * className={styles.tabList}>` and its `.tabList` / `.tab` / `.tabActive`
 * rules.
 *
 * **Not** RAC `Tabs`, and **not** a component that renders links from
 * descriptors either. The one call site's tabs are routes — its own in-code
 * comment says RAC `Tabs` "would have duplicated the routing it already
 * has" — and the same reasoning rules out pallet rendering the anchors
 * itself: that would make pallet depend on `@tanstack/react-router` just to
 * draw a `<a>`, a dependency none of pallet's other components take. So
 * `TabBar` owns only layout (`nav` + the flex row), and the caller supplies
 * already-rendered `Link` elements as children.
 *
 * Selection state is the caller's `Link`, not this component: TanStack
 * Router's `Link` already sets `aria-current="page"` (and `data-status`)
 * on the active route with no wiring required, so `TabBar` does not
 * reimplement it. `tabLinkClassName` below is the matching visual state,
 * for callers to hand to their `Link`'s `activeProps` / `inactiveProps` (or
 * equivalent) — see the call site for the exact wiring.
 *
 * The border under the row and any content beside the tabs (e.g. shared
 * filters) stay page layout, not `TabBar`'s: at the one call site the
 * border spans the tabs *and* a filter group beside them, which is a
 * page-composition concern, not a tab-bar one.
 */
function TabBar({ "aria-label": ariaLabel, children }: TabBarProps) {
  return (
    <nav aria-label={ariaLabel} className={styles.tabList}>
      {children}
    </nav>
  );
}

/**
 * The visual state for one tab link, keyed on whether it is the current
 * route. Exported as a plain function — not baked into `TabBar` — because
 * pallet never renders the link itself; see `TabBar`'s doc comment.
 */
export function tabLinkClassName(isActive: boolean): string {
  return clsx(styles.tab, { [styles.tabActive]: isActive });
}

export default TabBar;
